import { z } from 'zod';
import { createSchema } from 'zod-openapi';
import {
	type BillPrintedSchema,
	type BillSurplusSchema,
	type ComparatorBillInput,
	ComparatorBillSchema,
} from './comparator.dto';

// ============================================
// BILL READER (AI): the bill is never stored
// ============================================

/** Same limit as the documents module. */
export const BILL_READER_MAX_BYTES = 10 * 1024 * 1024;
export const BILL_READER_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'] as const;

/** Who and what the bill is about: shown on screen, never stored by the reader. */
export const BillIdentitySchema = z.object({
	cups: z.string().nullable(),
	holderName: z.string().nullable(),
	holderTaxId: z.string().nullable(),
	/** The marketer that issued the bill. */
	marketerName: z.string().nullable(),
});

/**
 * What the reader could make out, field by field. Anything it could not read
 * is missing, and the review screen asks for it before comparing.
 */
export type ComparatorBillDraft = Partial<Omit<ComparatorBillInput, 'printed' | 'surplus'>> & {
	surplus?: Partial<z.input<typeof BillSurplusSchema>> | null;
	printed?: Partial<z.input<typeof BillPrintedSchema>>;
};

export const BillReadingResponseSchema = z.object({
	draft: z.record(z.string(), z.unknown()),
	identity: BillIdentitySchema,
	/** Spanish notes for the reviewer: unsupported tariff, missing fields… */
	issues: z.array(z.string()),
	/** True when the draft is already a complete bill. It still needs a human look. */
	complete: z.boolean(),
});

// ============================================
// COMPARISON
// ============================================

const entityId = z.number().int().positive();

export const ComparatorCompareRequestSchema = z.object({
	bill: ComparatorBillSchema,
	/** Last twelve months' maximum demand (SIPS or the bill), to propose a lower power. */
	annualMaxDemandKw: z.number().finite().positive().max(100_000).nullable().default(null),
	/**
	 * Where to save the result (GK-1060): the client file the comparator was
	 * opened from, and its CUPS if any. Nothing is saved without a target or a
	 * CUPS that already exists in ERIS.
	 */
	target: z
		.object({ clientId: entityId, cupsId: entityId.nullable().default(null) })
		.nullable()
		.default(null),
	/** The CUPS read from the bill: when it already exists, the result is saved on it. */
	cupsCode: z.string().trim().max(30).nullable().default(null),
	/** Comparing again after going back to the review replaces this saved comparison. */
	replaceComparisonId: entityId.nullable().default(null),
});

/** Where a comparison was saved, to tell the user and link to the file. */
export const SavedComparisonRefSchema = z.object({
	id: z.number(),
	clientId: z.number(),
	clientName: z.string(),
	cupsId: z.number().nullable(),
	cupsCode: z.string().nullable(),
});

/** One row of the «Comparaciones» tab of a client file. */
export const ComparatorComparisonListItemSchema = z.object({
	id: z.number(),
	createdAt: z.string(),
	createdByName: z.string().nullable(),
	cupsId: z.number().nullable(),
	cupsCode: z.string().nullable(),
	tariff: z.string(),
	month: z.string().nullable(),
	currentTotal: z.number(),
	bestMarketerName: z.string().nullable(),
	bestOfferName: z.string().nullable(),
	bestTotal: z.number().nullable(),
	bestSavings: z.number().nullable(),
	bestSavingsPct: z.number().nullable(),
});

export const ComparatorComparisonFiltersSchema = z.object({
	clientId: z.coerce.number().int().positive(),
	cupsId: z.coerce.number().int().positive().optional(),
	page: z.coerce.number().int().min(1).optional().default(1),
	limit: z.coerce.number().int().min(1).max(100).optional().default(10),
});

export const ComparatorComparisonListResponseSchema = z.object({
	data: z.array(ComparatorComparisonListItemSchema),
	total: z.number(),
	page: z.number(),
	limit: z.number(),
});

const reportText = z.string().trim().max(200).nullable().default(null);

/**
 * The savings report (PDF) for the client: the same request as a comparison,
 * plus who it is for. Nothing is stored; the comparison is recomputed.
 */
export const ComparatorReportRequestSchema = ComparatorCompareRequestSchema.extend({
	identity: z
		.object({
			holderName: reportText,
			cups: reportText,
			marketerName: reportText,
		})
		.nullable()
		.default(null),
});

// ============================================
// TYPE EXPORTS
// ============================================

export type BillIdentityDTO = z.infer<typeof BillIdentitySchema>;
export type BillReadingResponseDTO = Omit<z.infer<typeof BillReadingResponseSchema>, 'draft'> & {
	draft: ComparatorBillDraft;
};
export type ComparatorCompareRequestDTO = z.output<typeof ComparatorCompareRequestSchema>;
export type ComparatorCompareRequestInput = z.input<typeof ComparatorCompareRequestSchema>;
export type SavedComparisonRefDTO = z.infer<typeof SavedComparisonRefSchema>;
export type ComparatorComparisonListItemDTO = z.infer<typeof ComparatorComparisonListItemSchema>;
export type ComparatorComparisonFiltersDTO = z.output<typeof ComparatorComparisonFiltersSchema>;
export type ComparatorComparisonListResponseDTO = z.infer<typeof ComparatorComparisonListResponseSchema>;
export type ComparatorReportRequestDTO = z.output<typeof ComparatorReportRequestSchema>;
export type ComparatorReportRequestInput = z.input<typeof ComparatorReportRequestSchema>;

// ============================================
// OPENAPI SCHEMAS
// ============================================

export const { schema: BillReadingResponseOpenAPI } = createSchema(BillReadingResponseSchema);
export const { schema: ComparatorCompareRequestOpenAPI } = createSchema(ComparatorCompareRequestSchema);
export const { schema: ComparatorReportRequestOpenAPI } = createSchema(ComparatorReportRequestSchema);
export const { schema: ComparatorComparisonListResponseOpenAPI } = createSchema(ComparatorComparisonListResponseSchema);
